import {
  Card,
  ECardCarouselSize,
  ECardMediaSize,
  ETagVariant,
  Loader,
  Tag,
} from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Swiper } from 'swiper';

import SuperhostBadgeFullSvg from '@/assets/superhost/superhost-badge-full.svg';
import { FavoriteButton } from '@/components/common/FavoriteButton';
import ResponsiveImage from '@/components/utility/ResponsiveImage/ResponsiveImage';
import { WishlistModalWrapper } from '@/components/wishlists/WishlistModal';
import { EListingTileTagSlug } from '@/constants/listingTags';
import { EPage, PAGE_PATHNAME } from '@/constants/pages';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useRouter } from '@/hooks/useRouter';
import { IWishlistingEventData } from '@/services/analytics/listings/types';
import { isProduction } from '@/utility/isSSR';
import { IRentalTile, isCampgroundTile, ISearchResultTile } from '@/utility/mapSearchResultToTile';

import css from './ListingTileMedia.module.css';
import {
  QuickPreviewButtonDesktop,
  QuickPreviewButtonMobile,
  QuickPreviewModalWrapper,
} from './QuickPreview';

type TListingTileMediaProps = {
  rentalTile: ISearchResultTile;
  isOutlined?: boolean;
  showFirstImageOnly?: boolean;
  onSlideChange?: (nextIndex: number) => void;
  isHorizontal?: boolean;
  isHorizontalBorderless?: boolean;
  showLimitedOfferTag?: boolean;
  showSuperhost?: boolean;
  showGuestFavorite?: boolean;
  withPriority?: boolean;
  withFetchPriority?: 'high' | 'low' | undefined;
  isNewHomepage?: boolean;
  wishlistingEventData?: IWishlistingEventData;
  autoCssJudgeStyle?: boolean;
  isFeatured?: boolean;
  tileIsHovered?: boolean;
  showQuickPreview?: boolean;
  quickPreviewOnClickViewListing?: () => void;
  quickPreviewOnClose?: () => void;
  customWishlistName?: string;
};

export const ListingTileMedia = ({
  rentalTile,
  isOutlined,
  showFirstImageOnly,
  onSlideChange,
  isHorizontal = false,
  isHorizontalBorderless = false,
  showLimitedOfferTag,
  showSuperhost = false,
  showGuestFavorite = false,
  withPriority = false,
  withFetchPriority,
  isNewHomepage = false,
  wishlistingEventData,
  autoCssJudgeStyle = false,
  isFeatured = false,
  tileIsHovered = false,
  showQuickPreview = false,
  quickPreviewOnClickViewListing,
  quickPreviewOnClose,
  customWishlistName,
}: TListingTileMediaProps) => {
  const intl = useIntl();
  const { isMobile, isAboveXXXL } = useBreakpoint();
  const [swiperInstance, setSwiperInstance] = useState<Swiper>();
  const router = useRouter();
  const isEscapesSearchPage = router?.pathname?.includes(PAGE_PATHNAME[EPage.ESCAPES_SEARCH]);

  const { loading, photoUrl, isFavorite, photoAlt, favoriteCount } = rentalTile;

  const listingLoader = !!loading && (
    <Loader className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
  );

  const tagElement = useMemo(() => {
    if (isCampgroundTile(rentalTile)) {
      return null;
    }

    if (showLimitedOfferTag) {
      return (
        <Tag
          variant={ETagVariant.Success}
          label={intl.formatMessage({ defaultMessage: 'Limited Offer', id: 'bDCVSI' })}
          className="uppercase"
        />
      );
    }

    // Hide superhost and guest-favorite badges when listing is featured
    if (isFeatured) {
      return null;
    }

    const superhostTag = rentalTile.details?.tags?.find(
      rentalTag => rentalTag.slug === EListingTileTagSlug.Superhost,
    );

    if (showSuperhost && superhostTag) {
      return <SuperhostBadgeFullSvg />;
    }

    const guestFavoriteTag = rentalTile.details?.tags?.find(
      rentalTag => rentalTag.slug === EListingTileTagSlug.GuestFavorite,
    );

    if (showGuestFavorite && guestFavoriteTag) {
      return <Tag variant={ETagVariant.White} label={guestFavoriteTag.tag} className="uppercase" />;
    }

    return null;
  }, [rentalTile, showLimitedOfferTag, isFeatured, showSuperhost, showGuestFavorite, intl]);

  const favoriteToggleButton = (
    <WishlistModalWrapper
      isFavorite={isFavorite}
      item={rentalTile}
      itemType={isEscapesSearchPage ? 'escape' : 'rental'}
      wishlistingEventData={wishlistingEventData}
      customWishlistName={customWishlistName}
      TriggerComponent={({ isFavorite: isWishlisted, toggleFavorite }) => (
        <FavoriteButton
          isFavorite={isWishlisted}
          favoriteCount={favoriteCount}
          onClick={toggleFavorite}
        />
      )}
    />
  );

  const isCampground = isCampgroundTile(rentalTile);

  const quickPreviewButtonDesktop = (
    <QuickPreviewModalWrapper
      rentalTile={rentalTile as IRentalTile}
      onClickViewListing={quickPreviewOnClickViewListing}
      onClose={quickPreviewOnClose}
      TriggerComponent={({ toggleQuickPreview }) => (
        <QuickPreviewButtonDesktop onClick={toggleQuickPreview} />
      )}
    />
  );

  const quickPreviewButtonMobile = (
    <QuickPreviewModalWrapper
      rentalTile={rentalTile as IRentalTile}
      onClickViewListing={quickPreviewOnClickViewListing}
      onClose={quickPreviewOnClose}
      TriggerComponent={({ toggleQuickPreview }) => (
        <QuickPreviewButtonMobile onClick={toggleQuickPreview} />
      )}
    />
  );

  const actionsLayer = (
    <>
      <div
        className={clsx('absolute top-0 left-0 flex w-full p-2 gap-2 items-start', {
          'justify-between': !!tagElement,
          'justify-end': !tagElement,
        })}>
        {tagElement}
        <div className="flex gap-2">
          {!isCampground && showQuickPreview && isMobile ? quickPreviewButtonMobile : null}
          {favoriteToggleButton}
        </div>
      </div>
      {!isCampground && showQuickPreview && !isMobile && (
        <div
          className={clsx('absolute left-0 flex justify-center w-full bottom-4', {
            invisible: !tileIsHovered,
          })}>
          {quickPreviewButtonDesktop}
        </div>
      )}
    </>
  );

  useEffect(() => {
    if (!swiperInstance) return;

    const handleRealIndexChange = (swiper: Swiper) => {
      onSlideChange?.(swiper.realIndex);
    };

    swiperInstance.on('realIndexChange', handleRealIndexChange);

    return () => {
      swiperInstance.off('realIndexChange', handleRealIndexChange);
    };
  }, [swiperInstance, onSlideChange]);

  if (!loading && !showFirstImageOnly && Array.isArray(photoUrl) && photoUrl.length > 1) {
    return (
      <Card.Carousel
        ref={ref => setSwiperInstance(ref?.swiper)}
        // some images in dev have the same url?
        content={
          // Show a maximum of 7 images
          photoUrl.slice(0, 7).map((src, index) => (
            <MediaImage
              key={`${!isProduction() ? index : 'image'}-${src}`}
              src={src}
              isLarge={isAboveXXXL}
              alt={index === 0 ? photoAlt : undefined}
              withPriority={index === 0 && withPriority}
              withFetchPriority={index === 0 ? withFetchPriority : undefined}
            />
          ))
        }
        size={isAboveXXXL ? ECardCarouselSize.Large : ECardCarouselSize.Medium}
        fullRadius
        className={clsx(css.listingTileMediaCarousel, {
          '!h-[214px]': isHorizontal,
          '!h-[140px] aspect-square': isHorizontalBorderless,
          'outline outline-2 outline-offset-2 outline-green-500': isOutlined,
          'aspect-[407/236] !h-full [&>div]:!pb-0': isNewHomepage,
          '[&>nav]:hidden': !isMobile && showQuickPreview && tileIsHovered,
        })}>
        {listingLoader}
        {actionsLayer}
      </Card.Carousel>
    );
  }

  return (
    <Card.Media
      size={isAboveXXXL ? ECardMediaSize.Large : ECardMediaSize.Medium}
      fullRadius
      className={clsx('bg-gray-200', {
        '!h-[214px]': isHorizontal,
        '!h-[140px] aspect-square': isHorizontalBorderless,
        'outline outline-2 outline-offset-2 outline-green-500': isOutlined,
        'aspect-[407/236] !h-full': isNewHomepage,
        '!h-[140px] aspect-square md:!h-[214px] md:aspect-auto': autoCssJudgeStyle,
      })}>
      {listingLoader}
      {typeof photoUrl === 'string' && (
        <MediaImage
          src={photoUrl}
          alt={photoAlt}
          isLarge={isAboveXXXL}
          withPriority={withPriority}
          withFetchPriority={withFetchPriority}
        />
      )}
      {Array.isArray(photoUrl) && !!photoUrl[0] && (
        <MediaImage
          src={photoUrl[0]}
          alt={photoAlt}
          isLarge={isAboveXXXL}
          withPriority={withPriority}
          withFetchPriority={withFetchPriority}
        />
      )}
      {actionsLayer}
    </Card.Media>
  );
};

const MediaImage = ({
  src,
  isLarge,
  alt,
  withPriority = false,
  withFetchPriority,
}: {
  src: string;
  isLarge: boolean;
  alt?: string;
  withPriority?: boolean;
  withFetchPriority?: 'high' | 'low' | undefined;
}) => {
  return (
    <ResponsiveImage
      src={src}
      alt={alt}
      cropMode="fill"
      role="presentation"
      sourceSizes={isLarge ? ['landscape572'] : ['landscape375']}
      className="object-cover object-center w-full h-full"
      priority={withPriority}
      withFetchPriority={withFetchPriority}
    />
  );
};
